import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, Download, FileText, ExternalLink, 
  ShoppingBag, CheckCircle, Loader2, AlertCircle,
  Truck, Shield, Briefcase, Building2, ClipboardCheck, Headphones
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for packets
const PACKET_ICONS = {
  'cdl-success': Truck,
  'trucking-operations': ClipboardCheck,
  'freight-broker': Briefcase,
  'authority-builder': Building2,
  'compliance': Shield,
  'dispatch': Headphones
};

const ResourcesModule = ({ userEmail }) => {
  const [resources, setResources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    if (userEmail) {
      fetchResources();
    }
  }, [userEmail]);

  const fetchResources = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/toolkit/my-resources?email=${encodeURIComponent(userEmail)}`);
      setResources(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch resources:', err);
      setError('Failed to load your resources');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (packet) => {
    if (!packet.download_url) return;
    
    setDownloadingId(packet.id);
    try {
      // Open download in new tab
      window.open(`${process.env.REACT_APP_BACKEND_URL}${packet.download_url}`, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setTimeout(() => setDownloadingId(null), 1000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin mx-auto mb-3" />
          <p className="text-white/60">Loading your resources...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 p-6 text-center">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
        <p className="text-red-400 mb-4">{error}</p>
        <button 
          onClick={fetchResources}
          className="px-4 py-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  // No purchases yet
  if (!resources?.has_purchases) {
    return (
      <div className="text-center py-12">
        <div className="w-20 h-20 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Package className="w-10 h-10 text-[#D4AF37]" />
        </div>
        <h3 className="text-xl font-bold text-white mb-3">No Resources Yet</h3>
        <p className="text-white/60 mb-6 max-w-md mx-auto">
          You haven't purchased any template packets yet. Visit Queen B Resources to browse our premium business templates.
        </p>
        <Link 
          to="/toolkit"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#C4A030] transition-colors"
          data-testid="browse-resources-btn"
        >
          <ShoppingBag className="w-5 h-5" />
          Browse Resources
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="resources-module">
      {/* Header Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-[#0A0A0A] border border-white/10 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D4AF37]/10 rounded flex items-center justify-center">
              <Package className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{resources.packet_details?.length || 0}</p>
              <p className="text-white/50 text-sm">Packets Owned</p>
            </div>
          </div>
        </div>
        <div className="bg-[#0A0A0A] border border-white/10 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/10 rounded flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{resources.purchases?.length || 0}</p>
              <p className="text-white/50 text-sm">Purchases</p>
            </div>
          </div>
        </div>
      </div>

      {/* Your Templates Section */}
      <div>
        <h3 className="text-lg font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#D4AF37]" />
          Your Template Packets
        </h3>
        
        <div className="space-y-3">
          {resources.packet_details?.map((packet) => {
            const IconComponent = PACKET_ICONS[packet.id] || FileText;
            const isDownloading = downloadingId === packet.id;
            
            return (
              <div 
                key={packet.id}
                className="bg-[#0A0A0A] border border-white/10 p-4 hover:border-[#D4AF37]/30 transition-colors"
                data-testid={`resource-item-${packet.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#D4AF37]/10 rounded flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white">{packet.title}</h4>
                      <p className="text-white/50 text-sm">{packet.filename}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleDownload(packet)}
                    disabled={isDownloading || !packet.download_url}
                    className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-bold text-sm uppercase tracking-wider hover:bg-[#C4A030] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`download-btn-${packet.id}`}
                  >
                    {isDownloading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    Download
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Get More Templates CTA */}
      <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-white font-bold mb-1">Need More Templates?</h4>
            <p className="text-white/60 text-sm">
              {resources.packet_details?.length < 6 
                ? `You own ${resources.packet_details?.length} of 6 packets. Complete your collection!`
                : 'You own all available packets. Check back for new releases!'}
            </p>
          </div>
          {resources.packet_details?.length < 6 && (
            <Link
              to="/toolkit"
              className="flex items-center gap-2 px-4 py-2 border border-[#D4AF37] text-[#D4AF37] font-bold text-sm uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-colors whitespace-nowrap"
              data-testid="get-more-templates-btn"
            >
              <ExternalLink className="w-4 h-4" />
              Get More
            </Link>
          )}
        </div>
      </div>

      {/* Purchase History */}
      {resources.purchases && resources.purchases.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-white uppercase tracking-wider mb-4">
            Purchase History
          </h3>
          <div className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-white/50 text-xs uppercase tracking-wider p-3">Order ID</th>
                  <th className="text-left text-white/50 text-xs uppercase tracking-wider p-3">Access Code</th>
                  <th className="text-left text-white/50 text-xs uppercase tracking-wider p-3">Packets</th>
                  <th className="text-right text-white/50 text-xs uppercase tracking-wider p-3">Amount</th>
                </tr>
              </thead>
              <tbody>
                {resources.purchases.map((purchase, idx) => (
                  <tr key={idx} className="border-b border-white/5 last:border-0">
                    <td className="p-3 text-white text-sm font-mono">{purchase.purchase_id}</td>
                    <td className="p-3 text-[#D4AF37] text-sm font-mono">{purchase.access_code}</td>
                    <td className="p-3 text-white/70 text-sm">{purchase.packets?.length || 0} packet(s)</td>
                    <td className="p-3 text-green-400 text-sm text-right font-bold">${purchase.total_amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourcesModule;
